import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from './App';
import { Mail, Lock, User, Building, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { lookupAPI } from '../services/api';
import { imageAssets } from '../assets/imageAssets';
import ScreenSplash from './ui/ScreenSplash';

interface Department {
  id: number;
  name: string;
  description: string;
  is_active: number;
}

interface FieldErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  department_id?: string;
}

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    department_id: '',
  });
  const [departments, setDepartments] = useState<Department[]>([]);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [showRegisterSplash, setShowRegisterSplash] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register, user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Password validation helper
  const validatePassword = (password: string): string | undefined => {
    if (!password) return undefined;
    
    // Check if password is at least 15 characters
    if (password.length >= 15) return undefined;
    
    // Check if password is at least 8 characters with a number and lowercase letter
    if (password.length >= 8) {
      const hasNumber = /\d/.test(password);
      const hasLowercase = /[a-z]/.test(password);
      if (hasNumber && hasLowercase) return undefined;
    }
    
    return 'Must be at least 15 characters OR at least 8 characters including a number and a lowercase letter.';
  };

  // Fetch departments on mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const result = await lookupAPI.getDepartments();
        console.log('Departments API response:', result);
        
        // Handle different response structures
        if (Array.isArray(result)) {
          setDepartments(result);
        } else if (result.data && Array.isArray(result.data)) {
          setDepartments(result.data);
        } else if (result.departments && Array.isArray(result.departments)) {
          setDepartments(result.departments);
        }
      } catch (err) {
        console.error('Failed to fetch departments:', err);
      }
    };
    fetchDepartments();
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      setShowRegisterSplash(true);
      const timer = window.setTimeout(() => {
        navigate(user.role === 'admin' ? '/admin/dashboard' : '/faculty/dashboard');
      }, 1000);
      return () => window.clearTimeout(timer);
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    const errors: FieldErrors = {};

    // Validate password
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      errors.password = passwordError;
    }

    // Validate confirm password
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.department_id) {
      errors.department_id = 'Please select a department';
    }

    // If there are validation errors, show them and stop
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);

    try {
      const result = await register(
        formData.name,
        formData.email,
        formData.password,
        parseInt(formData.department_id),
        'Faculty'
      );
      if (result.success) {
        // Navigation will happen via useEffect
      } else {
        const message = (result.message || '').toLowerCase();
        if (message.includes('name')) {
          setFieldErrors({ name: result.message || 'Name already exists' });
        } else if (message.includes('email')) {
          setFieldErrors({ email: result.message || 'Email already exists' });
        } else {
          setError(result.message || 'Registration failed. Please try again.');
        }
      }
    } catch (err: any) {
      // Handle specific API error responses
      if (err?.response?.data?.message?.toLowerCase().includes('name')) {
        setFieldErrors({ name: 'Name already exists' });
      } else if (err?.response?.data?.message?.toLowerCase().includes('email')) {
        setFieldErrors({ email: 'Email already exists' });
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear field error when user starts typing
    if (fieldErrors[name as keyof FieldErrors]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    // Real-time password validation
    if (name === 'password') {
      const passwordError = validatePassword(value);
      if (value && passwordError) {
        setFieldErrors((prev) => ({ ...prev, password: passwordError }));
      } else {
        setFieldErrors((prev) => ({ ...prev, password: undefined }));
      }
    }

    // Real-time confirm password validation
    if (name === 'confirmPassword' || (name === 'password' && formData.confirmPassword)) {
      const currentPassword = name === 'password' ? value : formData.password;
      const currentConfirm = name === 'confirmPassword' ? value : formData.confirmPassword;
      if (currentConfirm && currentPassword !== currentConfirm) {
        setFieldErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }));
      } else {
        setFieldErrors((prev) => ({ ...prev, confirmPassword: undefined }));
      }
    }
  };

  // Helper component for field error message
  const FieldError = ({ message }: { message?: string }) => {
    if (!message) return null;
    return (
      <div className="mt-2 flex items-start text-sm text-red-600">
        <AlertCircle className="w-4 h-4 mr-1.5 mt-0.5 flex-shrink-0" />
        <span>{message}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50/50 via-purple-50/30 to-pink-50/50 flex items-center justify-center p-4">
      <ScreenSplash show={showRegisterSplash} title="Creating Account" subtitle="Please wait..." />

      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center space-x-3 mb-4">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full blur-md opacity-30"></div>
              <img src={imageAssets.imdcLogo} alt="IMDC Creative Works Logo" className="w-14 h-14 rounded-full object-cover relative z-10 ring-2 ring-white shadow-lg" />
            </div>
            <div className="text-left">
              <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">IMDC Creative Works System</div>
              <div className="text-xs text-gray-500 font-medium tracking-wide">BOHOL ISLAND STATE UNIVERSITY</div>
            </div>
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Account</h1>
          <p className="text-gray-600">Join our faculty creative community</p>
        </div>

        {/* Register Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className={`h-5 w-5 ${fieldErrors.name ? 'text-red-400' : 'text-gray-400'}`} />
                </div>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:border-transparent ${
                    fieldErrors.name
                      ? 'border-red-500 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-purple-500'
                  }`}
                  placeholder="Juan Dela Cruz"
                />
              </div>
              <FieldError message={fieldErrors.name} />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                University Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className={`h-5 w-5 ${fieldErrors.email ? 'text-red-400' : 'text-gray-400'}`} />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:border-transparent ${
                    fieldErrors.email
                      ? 'border-red-500 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-purple-500'
                  }`}
                  placeholder="you@bisu.edu.ph"
                />
              </div>
              <FieldError message={fieldErrors.email} />
            </div>

            <div>
              <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-2">
                Department/College
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building className={`h-5 w-5 ${fieldErrors.department_id ? 'text-red-400' : 'text-gray-400'}`} />
                </div>
                <select
                  id="department"
                  name="department_id"
                  value={formData.department_id}
                  onChange={handleChange}
                  required
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:border-transparent appearance-none bg-white ${
                    fieldErrors.department_id
                      ? 'border-red-500 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-purple-500'
                  }`}
                >
                  <option value="">Select your department</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
              <FieldError message={fieldErrors.department_id} />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className={`h-5 w-5 ${fieldErrors.password ? 'text-red-400' : 'text-gray-400'}`} />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className={`block w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:border-transparent ${
                    fieldErrors.password
                      ? 'border-red-500 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-purple-500'
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <FieldError message={fieldErrors.password} />
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className={`h-5 w-5 ${fieldErrors.confirmPassword ? 'text-red-400' : 'text-gray-400'}`} />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className={`block w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:border-transparent ${
                    fieldErrors.confirmPassword
                      ? 'border-red-500 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-purple-500'
                  }`}
                  placeholder="Re-enter password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center text-gray-500 hover:text-gray-700"
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <FieldError message={fieldErrors.confirmPassword} />
            </div>

            <div className="flex items-start">
              <input
                id="terms"
                type="checkbox"
                required
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 mt-1"
              />
              <label htmlFor="terms" className="ml-2 text-sm text-gray-600">
                I agree to the{' '}
                <button type="button" className="text-purple-600 hover:text-purple-700">
                  Terms of Service
                </button>{' '}
                and{' '}
                <button type="button" className="text-purple-600 hover:text-purple-700">
                  Privacy Policy
                </button>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Please wait...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-purple-600 hover:text-purple-700 font-semibold">
                Sign in here
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center w-full px-6 py-3 bg-white border-2 border-purple-200 text-gray-700 rounded-lg hover:bg-purple-50 hover:border-purple-400 transition-all font-medium shadow-sm"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}